/**
 * Nested <details data-level> dropdowns in the mobile nav drawer (see
 * header-drawer-childlink.liquid). Native <details> already gives full
 * keyboard/AT support and instant open/close for free (summary is focusable,
 * Enter/Space toggle built in) -- matching the PDP `.accordion` component
 * (component-accordion.css), which relies on the same native behavior with no
 * JS-driven height animation. This script only adds `aria-expanded` sync and
 * Escape-to-close, which <details> doesn't provide natively.
 */
import { trapFocus, removeTrapFocus } from '@utilities/theme';

(() => {
  const initDropdown = (details) => {
    const level = Number(details.dataset.level);
    const opener = details.querySelector(`:scope > summary[data-opener-level="${level}"]`);
    if (!level || !opener) return;

    details.addEventListener('toggle', () => {
      opener.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
    });

    details.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !details.hasAttribute('open')) return;
      details.removeAttribute('open');
      opener.focus();
    });
  };

  document.querySelectorAll('details[data-level]').forEach(initDropdown);
})();

(() => {
  const navContainer = document.querySelector('.menu-drawer__navigation-container');

  const getParentMenu = (element) => {
    if (!(element instanceof Element)) return document;
    // .menu-drawer__panel wraps both .menu-drawer__menu (the opener's <ul>)
    // and its sibling .child-menu-drawer panels -- .child-menu-drawer is a
    // sibling rather than a descendant of .menu-drawer__menu because a <ul>
    // may only contain <li> as direct children.
    return element.closest('.menu-drawer__panel') ||
      element.closest('.menu-drawer__navigation') ||
      document;
  };

  const getChildDrawer = (parentMenu, linkHandle) =>
    parentMenu.querySelector(`.child-menu-drawer[data-link-handle="${CSS.escape(linkHandle)}"]`);

  // MenuDrawer (menu-drawer.js) traps focus on the whole drawer <details> when
  // it opens. trapFocus() only tracks one active trap at a time, so opening a
  // .child-menu-drawer panel here replaces that outer trap with a narrower
  // one -- closing the panel must re-establish the drawer-level trap rather
  // than fully releasing it, or the drawer loses focus containment entirely
  // for the rest of its open lifetime.
  const getDrawerDetails = (element) => element.closest('header-drawer')?.querySelector(':scope > details');

  const closeActivePanel = (parentMenu, { focusOpener = false } = {}) => {
    const activeOpener = parentMenu.querySelector('.link-handle-opener-js.active');

    parentMenu.querySelectorAll('.child-menu-drawer.active').forEach(el => {
      // Keep the panel visible/opaque (via .closing) through the slide-out
      // transition -- removing .active alone would drop visibility/opacity
      // to their closed-state values immediately, cutting the animation off
      // before the transform has a chance to play.
      el.classList.remove('active');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      el.classList.add('closing');
      el.addEventListener('transitionend', function onTransitionEnd(event) {
        if (event.target !== el || event.propertyName !== 'transform') return;
        el.classList.remove('closing');
        el.removeEventListener('transitionend', onTransitionEnd);
      });
    });

    parentMenu.querySelectorAll('.link-handle-opener-js.active')
      .forEach(el => {
        el.classList.remove('active');
        el.setAttribute('aria-expanded', 'false');
      });

    parentMenu.classList.remove('active-link-handle');

    // Re-trap focus on the whole drawer (see getDrawerDetails above) instead
    // of just releasing it, so the drawer keeps its own Tab containment.
    const drawerDetails = getDrawerDetails(parentMenu);
    if (drawerDetails) {
      trapFocus(drawerDetails, focusOpener ? activeOpener : drawerDetails.querySelector('summary'));
    } else {
      removeTrapFocus(focusOpener ? activeOpener : null);
    }
  };

  const openPanel = (parentMenu, opener, childDrawer) => {
    opener.classList.add('active');
    opener.setAttribute('aria-expanded', 'true');
    parentMenu.classList.add('active-link-handle');
    // In case this panel is being reopened before its previous close
    // animation finished (.closing left over from closeActivePanel).
    childDrawer.classList.remove('closing');
    childDrawer.classList.add('active');

    // Scroll the navigation container to the top so the newly opened panel is visible
    if (navContainer) {
      navContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Confine Tab/Shift+Tab to the panel -- it's a sibling overlay, not a
    // native modal, so without this the browser has no idea the underlying
    // .menu-drawer__menu items should be unreachable while it's open.
    const backButton = childDrawer.querySelector('.link-handle-back-js');
    trapFocus(childDrawer, backButton || childDrawer);
  };

  const handleClick = (event) => {
    const backButton = event.target.closest('.link-handle-back-js');
    if (backButton) {
      event.preventDefault();
      event.stopPropagation();
      const parentMenu = getParentMenu(backButton);
      closeActivePanel(parentMenu, { focusOpener: true });
      return;
    }

    const opener = event.target.closest('.link-handle-opener-js');
    if (!opener) return;

    event.preventDefault();
    event.stopPropagation();

    const linkHandle = opener.dataset.linkHandle;
    if (!linkHandle) return;

    const parentMenu = getParentMenu(opener);
    const childDrawer = getChildDrawer(parentMenu, linkHandle);
    if (!childDrawer) return;

    const isActive = childDrawer.classList.contains('active');

    // Only one panel open at a time.
    closeActivePanel(parentMenu);

    if (!isActive) openPanel(parentMenu, opener, childDrawer);
  };

  const handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const parentMenu = getParentMenu(event.target);
    if (!parentMenu.querySelector('.child-menu-drawer.active')) return;

    closeActivePanel(parentMenu, { focusOpener: true });
  };

  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);
})();

